import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Post-Ack Discount Modal (iter_140).
 *
 * Fires immediately after a buyer successfully submits the Compliance
 * Acknowledgment gate. Offers 10% off in exchange for an email — buyer
 * types the email themselves (no pre-fill), receives the code inline
 * with a copy button, and dismisses when ready.
 *
 * The parent (ComplianceGate) holds off on `refresh()` until this modal
 * closes so the gate stays behind us in the DOM but is visually covered.
 */
const API = process.env.REACT_APP_BACKEND_URL;

export default function PostAckDiscountModal({ open, onClose }) {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [claimedCode, setClaimedCode] = useState("");
  const [copied, setCopied] = useState(false);
  const emailRef = useRef(null);

  // iter_141 — Only enable the portal after mount so `document.body` is
  // guaranteed to exist. Prevents "Target container is not a DOM element".
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    const t = setTimeout(() => emailRef.current?.focus(), 240);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [open]);

  const close = () => {
    setEntered(false);
    setTimeout(() => {
      // Reset local state for next mount.
      setClaimedCode("");
      setEmail("");
      setCopied(false);
      onClose?.();
    }, 220);
  };

  const submit = async (e) => {
    e?.preventDefault();
    const cleaned = email.trim();
    if (!cleaned || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      toast.error("Enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/marketing/claim-welcome-code`, { email: cleaned });
      setClaimedCode(String(data?.code || ""));
      toast.success("Code ready — copy it below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!claimedCode) return;
    try {
      await navigator.clipboard.writeText(claimedCode);
      setCopied(true);
      toast.success("Copied — paste it at checkout.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — highlight and copy manually.");
    }
  };

  if (!open || !mounted) return null;

  const overlayCls = `fixed inset-0 z-[10000] flex items-center justify-center px-4 transition-opacity duration-300 ease-out ${
    entered ? "opacity-100" : "opacity-0 pointer-events-none"
  }`;
  const cardCls = `relative w-full max-w-md bg-bone border border-charcoal shadow-2xl transform transition-transform duration-300 ease-out ${
    entered ? "translate-y-0 scale-100" : "translate-y-4 scale-95"
  }`;

  // iter_141 — MUST portal to document.body. The parent (ComplianceGate)
  // wrapper uses `backdrop-blur-sm` which creates a CSS containing block for
  // `position: fixed` children, trapping this modal INSIDE the gate flex row
  // instead of overlaying the whole viewport. Portalling escapes that.
  return createPortal(
    <div
      className={overlayCls}
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-ack-discount-title"
      data-testid="post-ack-discount-modal"
    >
      <div className="absolute inset-0 bg-charcoal/80 backdrop-blur-sm" aria-hidden />
      <div className={cardCls} onClick={(e) => e.stopPropagation()}>
        <div className="h-1 bg-oxblood" aria-hidden />
        <button
          onClick={close}
          className="absolute right-3 top-3 text-charcoal/60 hover:text-oxblood transition-colors p-1"
          data-testid="post-ack-discount-close"
          aria-label="Close"
        >
          <X size={16} strokeWidth={1.75} />
        </button>

        <div className="px-8 md:px-10 pt-9 pb-8">
          <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-charcoal/75 mb-6">
            Welcome — one last thing
          </p>

          {claimedCode ? (
            <div className="min-h-[220px] flex flex-col justify-center" data-testid="post-ack-discount-success">
              <h2 id="post-ack-discount-title" className="font-serif text-[34px] md:text-[38px] leading-[1.02] mb-3 tracking-tight">
                Your code.
              </h2>
              <p className="text-[14px] text-charcoal/80 leading-relaxed mb-4">
                Apply at checkout. Good for one order — any kit, Supply or Select.
              </p>

              <div className="bg-[#FDF6E3] border border-oxblood border-l-4 px-4 py-3 flex items-center gap-3 mb-5" data-testid="post-ack-discount-code-block">
                <code className="flex-1 font-mono text-[18px] tracking-[0.08em] text-oxblood font-bold select-all" data-testid="post-ack-discount-code">
                  {claimedCode}
                </code>
                <button
                  onClick={copyCode}
                  className="flex items-center gap-1.5 px-3 py-2 bg-oxblood text-bone tracking-[0.18em] text-[11px] uppercase font-semibold hover:bg-charcoal transition-colors"
                  data-testid="post-ack-discount-copy"
                  aria-label="Copy code"
                >
                  {copied ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} strokeWidth={2} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>

              <p className="text-[12px] text-charcoal/60 leading-relaxed mb-5">
                We also emailed it to <span className="font-mono">{email}</span> as a backup.
              </p>

              <button
                onClick={close}
                className="self-start px-6 py-2.5 bg-charcoal text-bone tracking-[0.22em] text-[11px] uppercase hover:bg-oxblood transition-colors"
                data-testid="post-ack-discount-done"
              >
                Continue to catalog
              </button>
            </div>
          ) : (
            <>
              <h2
                id="post-ack-discount-title"
                className="font-serif text-[34px] md:text-[38px] leading-[1.02] tracking-tight mb-3"
              >
                10% off your first order.
              </h2>
              <p className="text-[15px] text-charcoal/80 leading-relaxed mb-6">
                Drop your email — we'll send the code now and show it here for copy-paste at checkout.
              </p>

              <form onSubmit={submit} className="space-y-3" data-testid="post-ack-discount-form">
                <input
                  ref={emailRef}
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@lab.edu"
                  className="w-full px-4 py-3 bg-white border border-charcoal/40 focus:border-charcoal focus:outline-none focus:ring-0 font-mono text-[14px] placeholder:text-charcoal/40"
                  data-testid="post-ack-discount-email"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full py-[14px] bg-oxblood text-bone tracking-[0.24em] text-[12px] uppercase hover:bg-charcoal transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="post-ack-discount-submit"
                >
                  {busy ? "Sending…" : "Claim my 10% off"}
                </button>
              </form>

              <button
                onClick={close}
                className="mt-4 text-[12px] font-mono uppercase tracking-widest text-charcoal/60 hover:text-oxblood transition-colors"
                data-testid="post-ack-discount-skip"
              >
                No thanks — take me to the catalog
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
